window.StickerCovenConfigurator =
  window.StickerCovenConfigurator || {};

window.StickerCovenConfigurator.ApippaService =
  class ApippaService {
    constructor() {
      this.calculator = null;
      this.form = null;
      this.submitButton = null;
    }

    connect() {
      this.calculator =
        document.querySelector("#calculator");

      if (!this.calculator) {
        return false;
      }

      const productSection =
        this.calculator.closest(
          ".product, .product-section, [id^='MainProduct-']"
        );

      this.form =
        productSection?.querySelector(
          'form[action*="/cart/add"]'
        ) ||
        document.querySelector(
          'form[action*="/cart/add"]'
        ) ||
        null;

      this.submitButton =
        this.form?.querySelector(
          ".sticker-configurator__apippa-cart-hook"
        ) ||
        this.form?.querySelector(
          'button[type="submit"][name="add"]'
        ) ||
        document.querySelector(
          'button.product-form__submit[type="submit"][name="add"]'
        ) ||
        null;

      if (!this.form && this.submitButton) {
        this.form = this.submitButton.form;
      }

      return this.isReady();
    }

    isConnected() {
      return Boolean(this.calculator);
    }

    isReady() {
      return Boolean(
        this.calculator &&
        this.form &&
        this.submitButton
      );
    }

    wait(milliseconds) {
      return new Promise((resolve) => {
        window.setTimeout(resolve, milliseconds);
      });
    }

    getNamedFields() {
      if (!this.calculator) {
        return [];
      }

      return Array.from(
        this.calculator.querySelectorAll("[name]")
      );
    }

    getField(name) {
      return (
        this.getNamedFields().find(
          (field) => field.name === name
        ) || null
      );
    }

    getFields(name) {
      return this.getNamedFields().filter(
        (field) => field.name === name
      );
    }

    getFieldStartingWith(prefix) {
      return (
        this.getNamedFields().find(
          (field) =>
            field.name.startsWith(prefix)
        ) || null
      );
    }

    dispatchFieldEvents(field) {
      if (!field) return;

      field.dispatchEvent(
        new Event("input", {
          bubbles: true
        })
      );

      field.dispatchEvent(
        new Event("change", {
          bubbles: true
        })
      );
    }

    setFieldValue(field, value) {
      if (!field) {
        return false;
      }

      const nextValue = String(value);

      if (field.value === nextValue) {
        return true;
      }

      field.value = nextValue;
      this.dispatchFieldEvents(field);

      return true;
    }

    setInput(name, value) {
      return this.setFieldValue(
        this.getField(name),
        value
      );
    }

    setSelect(name, value) {
      const select = this.getField(name);

      return this.setSelectElement(
        select,
        value,
        name
      );
    }

    setSelectElement(
      select,
      value,
      fieldDescription = "select"
    ) {
      if (
        !select ||
        select.tagName !== "SELECT"
      ) {
        console.warn(
          `Apippa ${fieldDescription} was not found.`
        );

        return false;
      }

      const nextValue = String(value);

      const optionExists = Array.from(
        select.options
      ).some(
        (option) =>
          option.value === nextValue
      );

      if (!optionExists) {
        console.warn(
          `Apippa option not found for ${fieldDescription}:`,
          nextValue
        );

        return false;
      }

      return this.setFieldValue(
        select,
        nextValue
      );
    }

    setRadio(name, value) {
      const radios = this.getFields(name);

      const matchingRadio = radios.find(
        (radio) =>
          radio.value === String(value)
      );

      if (!matchingRadio) {
        console.warn(
          `Apippa radio option not found for ${name}:`,
          value
        );

        return false;
      }

      radios.forEach((radio) => {
        radio.checked =
          radio === matchingRadio;
      });

      this.dispatchFieldEvents(
        matchingRadio
      );

      return true;
    }

    formatDimension(value) {
      const number =
        Number.parseFloat(value);

      if (!Number.isFinite(number)) {
        return "";
      }

      return Number.isInteger(number)
        ? String(number)
        : number
            .toFixed(2)
            .replace(/0+$/, "")
            .replace(/\.$/, "");
    }

    getPresetSizeValue(width, height) {
      const sizeSelect = this.getField(
        "properties[Size]"
      );

      if (!sizeSelect) {
        return null;
      }

      const widthText =
        this.formatDimension(width);

      const heightText =
        this.formatDimension(height);

      const directValue =
        `${widthText}" x ${heightText}"`;

      const reversedValue =
        `${heightText}" x ${widthText}"`;

      const availableValues = Array.from(
        sizeSelect.options
      ).map((option) => option.value);

      if (
        availableValues.includes(
          directValue
        )
      ) {
        return directValue;
      }

      if (
        availableValues.includes(
          reversedValue
        )
      ) {
        return reversedValue;
      }

      return null;
    }

    async syncSize(width, height) {
      const presetSize =
        this.getPresetSizeValue(
          width,
          height
        );

      if (presetSize) {
        this.setSelect(
          "properties[Size]",
          presetSize
        );

        await this.wait(350);
        return true;
      }

      const selected = this.setSelect(
        "properties[Size]",
        "Custom Size"
      );

      if (!selected) {
        return false;
      }

      /*
       * Apippa displays and resets the custom-size
       * inputs after changing the Size dropdown.
       */
      await this.wait(500);

      const widthField = this.getField(
        "properties[Width]"
      );

      const lengthField = this.getField(
        "properties[Length]"
      );

      if (!widthField || !lengthField) {
        throw new Error(
          "Apippa custom-size fields could not be found."
        );
      }

      this.setFieldValue(
        widthField,
        width
      );

      this.setFieldValue(
        lengthField,
        height
      );

      await this.wait(250);

      return true;
    }

    async syncQuantity(
      quantity,
      usesCustomQuantity
    ) {
      const quantitySelect =
        this.getField(
          "properties[Quantity]"
        );

      if (!quantitySelect) {
        throw new Error(
          "Apippa quantity field could not be found."
        );
      }

      const quantityText =
        String(quantity);

      const presetExists = Array.from(
        quantitySelect.options
      ).some(
        (option) =>
          option.value === quantityText &&
          option.value !==
            "Custom Quantity"
      );

      if (
        presetExists &&
        !usesCustomQuantity
      ) {
        this.setSelect(
          "properties[Quantity]",
          quantityText
        );

        await this.wait(300);

        /*
         * Neutral value used by your Apippa formula
         * when a preset quantity is selected.
         */
        this.setInput(
          "properties[Custom Quantity]",
          1
        );

        return true;
      }

      this.setSelect(
        "properties[Quantity]",
        "Custom Quantity"
      );

      await this.wait(400);

      /*
       * Query again after Apippa exposes or updates
       * its custom-quantity input.
       */
      const customQuantityField =
        this.getField(
          "properties[Custom Quantity]"
        );

      if (!customQuantityField) {
        throw new Error(
          "Apippa custom-quantity field could not be found."
        );
      }

      this.setFieldValue(
        customQuantityField,
        quantity
      );

      await this.wait(250);

      return true;
    }

    async syncOptions(state) {
      if (state.shape) {
        this.setRadio(
          "properties[Shape]",
          state.shape
        );
      }

      if (state.finish) {
        this.setRadio(
          "properties[Finish]",
          state.finish
        );
      }
      
      if (state.material) {
        this.setRadio(
          "properties[Material]",
          state.material
        );
      }

      if (state.whiteInk) {
        this.setRadio(
          "properties[White Ink]",
          state.whiteInk
        );
      }
      
      if (state.easyPeel) {
        this.setRadio(
          "properties[Easy Peel]",
          state.easyPeel
        );
      }

      if (state.border) {
        /*
         * Your Apippa Border select currently has the
         * unusual name "properties[Border" without a
         * closing bracket.
         */
        const borderSelect =
          this.getFieldStartingWith(
            "properties[Border"
          );

        this.setSelectElement(
          borderSelect,
          state.border,
          "Border"
        );
      }
      const specialInstructionsField =
        this.calculator?.querySelector(
          '[name="properties[Special Instructions]"]'
        ) ||
        this.form?.querySelector(
          '[name="properties[Special Instructions]"]'
        );

      if (specialInstructionsField) {
        specialInstructionsField.value =
          state.specialInstructions || "";

        specialInstructionsField.dispatchEvent(
          new Event("input", {
            bubbles: true
          })
        );

        specialInstructionsField.dispatchEvent(
          new Event("change", {
            bubbles: true
          })
        );
      }
      await this.wait(400);
    }

    removeInjectedProperties() {
      if (!this.form) return;

      this.form
        .querySelectorAll(
          '[data-sticker-coven-property="true"]'
        )
        .forEach((input) => {
          input.remove();
        });
    }

    injectProperties(properties = {}) {
      if (!this.form) return;

      this.removeInjectedProperties();

      Object.entries(properties).forEach(
        ([propertyName, propertyValue]) => {
          if (
            propertyValue === null ||
            propertyValue === undefined ||
            propertyValue === ""
          ) {
            return;
          }

          const input =
            document.createElement("input");

          input.type = "hidden";
          input.name =
            `properties[${propertyName}]`;

          input.value =
            String(propertyValue);

          input.dataset.stickerCovenProperty =
            "true";

          this.form.appendChild(input);
        }
      );
    }

    getPriceTokenValue() {
      const tokenField =
        this.form?.querySelector(
          'input[id^="cpcPriceToken"]'
        ) ||
        document.querySelector(
          'input[id^="cpcPriceToken"]'
        );

      return tokenField?.value || "";
    }

    async waitForPricing(
      previousToken = ""
    ) {
      /*
       * Give Apippa time to finish its normal
       * recalculation and pricing-token updates.
       */
      await this.wait(700);

      const startedAt = Date.now();

      while (
        Date.now() - startedAt < 1300
      ) {
        const currentToken =
          this.getPriceTokenValue();

        if (
          currentToken &&
          currentToken !== previousToken
        ) {
          return true;
        }

        await this.wait(100);
      }

      /*
       * An unchanged token can be valid when the same
       * configuration is submitted again.
       */
      return true;
    }

    async syncConfiguration(
      state,
      extraProperties = {}
    ) {
      if (!this.isReady()) {
        this.connect();
      }

      if (!this.isReady()) {
        throw new Error(
          "The Apippa pricing form could not be found."
        );
      }

      const previousToken =
        this.getPriceTokenValue();

      await this.syncSize(
        state.width,
        state.height
      );

      await this.syncQuantity(
        state.quantity,
        state.usesCustomQuantity
      );

      await this.syncOptions(state);

      this.injectProperties(
        extraProperties
      );

      await this.waitForPricing(
        previousToken
      );

      return true;
    }

    submit() {
    if (!this.isReady()) {
        throw new Error(
        "The Apippa Add to Cart form is unavailable."
        );
    }

    /*
    * Apippa appears to build its cart payload from the
    * real product button's click handler. Using
    * requestSubmit() bypasses that handler.
    */
      this.submitButton.disabled = false;
      this.submitButton.click();
    }

    async submitConfiguredItem(
      state,
      extraProperties = {}
    ) {
      await this.syncConfiguration(
        state,
        extraProperties
      );

      this.submit();
    }

    logStatus() {
      console.group(
        "Sticker Coven — Apippa connection"
      );

      console.log(
        "Calculator found:",
        this.isConnected()
      );

      console.log(
        "Product form found:",
        Boolean(this.form)
      );

      console.log(
        "Submit button found:",
        Boolean(this.submitButton)
      );

      console.groupEnd();
    }
  };